export const ITEM_TYPE_GROUPS = [
  {
    label: "Core listing types",
    types: [
      { value: "material", label: "Material / Supply" },
      { value: "equipment", label: "Equipment / Tool" },
      { value: "property", label: "Property" },
    ],
  },
  {
    label: "Specialized items",
    types: [
      { value: "vehicle_transport", label: "Vehicle / Transport" },
      { value: "heavy_machinery", label: "Heavy Machinery" },
      { value: "electrical", label: "Electrical" },
      { value: "plumbing_sanitary", label: "Plumbing / Sanitary" },
    ],
  },
  {
    label: "Businesses",
    types: [
      { value: "business", label: "Business for Sale" },
    ],
  },
  {
    label: "Site readiness",
    types: [
      { value: "safety_ppe", label: "Safety / PPE" },
      { value: "formwork_scaffold", label: "Formwork / Scaffolding" },
      { value: "fixtures_finishes", label: "Fixtures / Finishes" },
      { value: "site_facilities", label: "Office / Site Facilities" },
      { value: "surplus_salvaged", label: "Surplus / Salvaged Materials" },
    ],
  },
] as const;

export const ITEM_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ITEM_TYPE_GROUPS.flatMap((group) => group.types.map((type) => [type.value, type.label])),
);

export const itemTypeLabel = (kind?: string) => ITEM_TYPE_LABELS[kind || ""] || "Material / Supply";

export type ItemPostingType = { value: string; label: string };

export const ITEM_POSTING_TYPE_GROUPS: { label: string; types: ItemPostingType[] }[] = [
  {
    label: "Equipment & Tools",
    types: [
      { value: "tools", label: "Construction Tools" },
      { value: "heavy_machinery", label: "Heavy Equipment / Machinery" },
      { value: "industrial_equipment", label: "Industrial / Manufacturing Equipment" },
      { value: "restaurant_equipment", label: "Restaurant / Kitchen Equipment" },
      { value: "electronics", label: "Office Electronics" },
      { value: "appliances", label: "Business Appliances" },
      { value: "furniture", label: "Office / Commercial Furniture" },
      { value: "agricultural_equipment", label: "Agricultural Equipment" },
      { value: "event_equipment", label: "Event Equipment" },
      { value: "electrical_equipment", label: "Electrical Equipment" },
      { value: "formwork_scaffold", label: "Formwork / Scaffolding" },
      { value: "site_facilities", label: "Office / Site Facilities" },
      { value: "equipment", label: "Other Business Equipment / Tools" },
    ],
  },
  {
    label: "Materials & Supplies",
    types: [
      { value: "material", label: "Building Materials / Supplies" },
      { value: "electrical", label: "Electrical Supplies" },
      { value: "plumbing_sanitary", label: "Plumbing / Sanitary Supplies" },
      { value: "safety_ppe", label: "Safety / PPE" },
      { value: "uniforms", label: "Uniforms / Workwear" },
      { value: "safety_footwear", label: "Safety Footwear" },
      { value: "farm_products", label: "Farm Products / Fresh Produce" },
      { value: "agricultural_supplies", label: "Seeds / Agricultural Supplies" },
      { value: "fixtures_finishes", label: "Fixtures / Finishes" },
      { value: "surplus_salvaged", label: "Surplus / Salvaged Materials" },
      { value: "consumables", label: "Consumables / Packaging Supplies" },
    ],
  },
  {
    label: "Vehicles & Transport",
    types: [
      { value: "cars", label: "Cars" },
      { value: "motorcycles", label: "Motorcycles" },
      { value: "trucks", label: "Trucks" },
      { value: "vans", label: "Vans" },
      { value: "buses", label: "Buses" },
      { value: "boats", label: "Boats" },
      { value: "trailers", label: "Trailers" },
      { value: "vehicle_parts", label: "Vehicle Parts / Accessories" },
      { value: "vehicle_transport", label: "Other Vehicles / Transport" },
    ],
  },
  {
    label: "Commercial Property",
    types: [
      { value: "commercial_spaces", label: "Commercial / Retail Spaces" },
      { value: "offices", label: "Offices" },
      { value: "warehouses", label: "Warehouses" },
      { value: "industrial_properties", label: "Industrial Properties" },
      { value: "lots", label: "Agricultural / Commercial Land" },
      { value: "property", label: "Other Commercial Property" },
    ],
  },
  {
    label: "Whole Businesses",
    types: [
      { value: "restaurant_business", label: "Restaurant / Café" },
      { value: "retail_business", label: "Retail Store" },
      { value: "construction_business", label: "Construction Company" },
      { value: "manufacturing_business", label: "Manufacturing Business" },
      { value: "transport_business", label: "Transport / Logistics Company" },
      { value: "farm_business", label: "Farm / Agricultural Business" },
      { value: "service_business", label: "Service Business" },
      { value: "business", label: "Other Whole Business" },
    ],
  },
];

export const itemPostingType = (value: string) => ITEM_POSTING_TYPE_GROUPS.flatMap((group) => group.types).find((itemType) => itemType.value === value);

export const BUSINESS_OPERATING_STATUSES = [
  { value: "operating", label: "Operating" },
  { value: "temporarily_closed", label: "Temporarily closed" },
  { value: "closed", label: "Closed" },
  { value: "not_yet_operating", label: "Not yet operating" },
] as const;

export const itemPostingLabel = (itemType?: string | null, kind?: string) => itemPostingType(itemType || "")?.label || itemTypeLabel(kind);

export const ITEM_HANDOVER_LABELS: Record<string, string> = {
  pickup: "Pickup",
  meetup: "Meetup",
  delivery_arrangement: "Delivery by arrangement",
};
